import React, { useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Transition, Menu } from "@headlessui/react";
import { Link } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import { apiPut } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import { BsChevronDown } from "react-icons/bs";
import { useForm } from "react-hook-form";
import OInputField from "components/reusable/OInputField";
import { preventMaxInput } from "utils/validations";
import formValidation from "../../utils/formValidation";
import helpers from "../../utils/helpers";
import ViewInfo from "components/reusable/ViewInfo";
import FormViewer from './FormViewer';
import DocumentViewer from './DocumentViewer';

const SubmittedEmployeeView = ({ setSubmitEmployeeView, currentItem, allSubmitEmployeeList, allowedMinute }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: {},
  });
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [contentFilled, setContentFilled] = useState(false);
  const [commentDescription, setCommentDescription] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // ✅ new

  const handleDocumentView = (doc) => {
    setSelectedDocument(doc);
    setDocumentOpen(true);
  };

  const handleView = (id) => {
    const form = currentItem?.EmployeeFilledForms[0].find(form => form._id === id);
    if (form && form.formData && form.content) {
      setSelectedForm({
        structure: form.formData,
        data: form
      });
      setOpen(true);
    }
  };

  const handleCloseForm = () => {
    setOpen(false);
    setSelectedForm(null);
  };

  const handleAccept = async (data) => {
    try {
      if (watch("comment") === "") return;
      setIsSubmitting(true); // ✅ start loading
      const payload = {
        jobRequestID: currentItem?._id,
        status: data.status,
        comment: data.comment,
      };
      const path = apiPath.changeFormStatus;
      const result = await apiPut(path, payload);
      if (result.data.success) {
        setSubmitEmployeeView(false);
        allSubmitEmployeeList();
      }
    } catch (error) {
      console.log("error in get all employee list==>>>>", error.message);
    } finally {
      setIsSubmitting(false); // ✅ stop loading
    }
  };

  const CustomMenuItem = ({ onClick, text, status }) => (
    <Menu.Item>
      {({ active }) => (
        <Link onClick={onClick} className={`${helpers?.ternaryCondition(active, "bg-red-700 text-white", "text-gray-900")} group flex w-full items-center px-6 py-2 text-sm`}>
          {text}
        </Link>
      )}
    </Menu.Item>
  );

  return (
    <>
      <div className="justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <div className="sm:py-4 sm:px-2 py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[940px] max-h-[85vh] overflow-y-auto	">
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">{t("O_VIEW_USER")}</h3>
                <button className=" ml-auto flex items-center justify-center  text-black border-2 rounded-full  h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none" onClick={() => setSubmitEmployeeView(false)}>
                  <span className=" text-[#B8BBBF]  text-4xl " title="Close">
                    ×
                  </span>
                </button>
              </div>
              <div className="relative p-6 flex-auto">
                <h3 className="text-xl font-semibold">Employee details</h3>
                <div className="grid grid-cols-3">
                  <ViewInfo label="Employee ID" value={currentItem?.employmentStatus === "terminated" ? `${currentItem?.Company.name.substring(0, 3)}${currentItem.Employee._id.toString().slice(-3)}` :currentItem?.Employee?.employeeId} />
                  {/* <ViewInfo label="Employee ID" value={currentItem?.Employee?.employeeId} /> */}
                  <ViewInfo label="Employee name" value={currentItem?.Employee?.name} />
                  <ViewInfo label="Email ID" value={currentItem?.Employee?.email} />
                  <ViewInfo label="Year of experience" value={currentItem.yearOfExperiece} />
                  <ViewInfo label="DOB" value={dayjs(currentItem?.Employee?.dob).format("MM-DD-YYYY ")} />
                  <ViewInfo label="Country" value={currentItem.EmployeeCountry?.name} />
                  <ViewInfo label="State" value={currentItem.EmployeeState?.name} />
                  <ViewInfo label="City" value={currentItem.Employee?.city} />
                  <ViewInfo label="Skills selected by employee" value={currentItem.EmployeeSkills?.map((item) => item?.name).join(", ")} />
                  <ViewInfo label="Job title" value={currentItem.Job?.name} />
                  <ViewInfo label="Department" value={currentItem.Department?.name} />
                  <ViewInfo label="Employment length" value={currentItem?.Job?.employmentLength} />
                  <ViewInfo label="Skill uploaded in job" value={currentItem.JobSkills?.map((item) => item?.name).join(", ")} />
                  <ViewInfo label="Created date" value={dayjs(currentItem?.createdAt).format("MM-DD-YYYY ")} />
                  <ViewInfo label="Duration Type" value={currentItem.duration} />
                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>LinkedIn</strong>
                      <h6>{currentItem.Employee?.linkedInLink ? (
                        <a href={currentItem.Employee.linkedInLink} target="_blank" rel="noopener noreferrer">
                          View Profile
                        </a>
                      ) : (
                        <span>LinkedIn profile not added</span>
                      )}</h6>
                    </div>
                  </div>
                </div>
              </div>
              {currentItem?.employmentStatus === "terminated" ? null : (<div className="relative p-6 flex-auto">
                <h3 className="text-xl font-semibold">Forms submitted by the employee (Submitted date: {currentItem?.createdAt ? dayjs(currentItem?.createdAt).format("MM-DD-YYYY") : "N/A"})</h3>
                <div className="">
                  <div className="sm:py-4 sm:px-2 px-7">
                    {currentItem.EmployeeFilledForms.length > 0 && currentItem.AssignedForms.length > 0 && currentItem.status === "accepted" && (<div className="relative z-0 mb-2 w-full group">
                      <strong className="block mb-2">Form title</strong>
                      <div className="flex flex-wrap">
                        {currentItem.EmployeeFilledForms[0]
                          ?.filter(form => form.jobID === currentItem._id)
                          .map((form, index) => (
                            <div key={form._id || form.formId || index}>
                              <Link
                                onClick={() => handleView(form._id )}
                                className="mb-3 block text-base underline mr-3 hover:text-LightBlue"
                              >
                                {form.formType
                                  .replace(/[-_]/g, " ")
                                  .replace(/\b\w/g, c => c.toUpperCase())}
                              </Link>
                            </div>
                          ))}
                      </div>
                    </div>)}
                    {currentItem.EmployeeDocuments.length > 0 && (
                      <div className="relative z-0 mb-2 w-full group">
                        <strong className="block mb-2">Other Documents</strong>
                        <div className="flex flex-wrap gap-4">
                          {currentItem.EmployeeDocuments.map((doc, index) => (
                              <div key={doc?._id || index} style={{cursor:"pointer"}} onClick={() => handleDocumentView(doc)} className="bg-gray-50 p-3 rounded-md min-w-[200px]">
                                <div className="mb-2">
                                  <p className="font-medium text-gray-900">
                                    {doc?.title?.replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {doc?.expiryDate ? dayjs(doc?.expiryDate).format("MM-DD-YYYY") : "No expiry"}
                                  </p>
                                </div>
                              </div>
                          ))}
                        </div>
                      </div>
                    )}


                    {currentItem?.employeeFormStatus && new Date(new Date(currentItem?.updatedAt).setMinutes(new Date(currentItem?.updatedAt).getMinutes() + allowedMinute)) < new Date() && (
                      <>
                        {!["accepted", "blocked", "promoted", "discharged"].includes(currentItem.formStatus) && (
                          <div className="relative z-0 mb-2 w-full group">
                              {!commentDescription && (
                                <button
                                  onClick={() => handleAccept({ status: "accepted" })}
                                  disabled={isSubmitting} // ✅
                                  className="bg-green-500 text-white font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                  type="button"
                                >
                                  {isSubmitting && <FiLoader className="animate-spin w-4 h-4" />}
                                  {isSubmitting ? "Accepting..." : t("ACCEPT")}
                                </button>
                              )}

                              {!commentDescription && (
                                <Menu as="div" className="relative inline-block text-left hover:text-LightBlue sidebar_dropdown ml-auto">
                                  <div>
                                    <Menu.Button className="w-[141px] flex relative items-center text-white active:text-LightBlue hover:bg-white hover:text-LightBlue group">
                                      <div className="profile flex items-center ml-auto md:mr-10 mr-2">
                                        <div className="flex justify-center w-full items-center text-sm ml-2  bg-red-700 p-3 px-5 py-3 text-white rounded">
                                          Reject <BsChevronDown className="ml-2" />
                                        </div>
                                      </div>
                                    </Menu.Button>
                                  </div>
                                  <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-10"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                  >
                                    <Menu.Items className="min-w-[180px] right-[30px] mt-3 bg-red absolute z-10 text-left content divide-y divide-gray-100  bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                      <div className="">
                                        <CustomMenuItem onClick={() => setCommentDescription(true)} text="Reject (Re-assign)" />
                                        <CustomMenuItem onClick={() => handleAccept({ status: "blocked" })} text="Reject (Block)" />
                                      </div>
                                    </Menu.Items>
                                  </Transition>
                                </Menu>
                              )}
                            </div>
                          )}
                        </>
                    )}

                    {commentDescription && (
                      <>
                        <h2>Comments</h2>
                        <div>
                          <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                            <OInputField
                              wrapperClassName="relative z-0 w-full group"
                              type="text"
                              name="comment"
                              id="comment"
                              maxLength={800}
                              autoFocus={true}
                              inputLabel={
                                <>
                                  {t("COMMENT")}
                                  <span className="text-red-500">*</span>
                                </>
                              }
                              placeholder=""
                              onInput={(e) => preventMaxInput(e, 800)}
                              register={register("comment", formValidation["comment"])}
                              errors={errors}
                            />
                          </div>
                          <button
                            onClick={() => {
                              handleSubmit(
                                handleAccept({
                                  status: "reassigned",
                                  comment: watch("comment"),
                                })
                              );
                            }}
                            disabled={isSubmitting} // ✅
                            className="bg-red-700 text-white font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                            type="submit"
                          >
                            {isSubmitting && <FiLoader className="animate-spin w-4 h-4" />}
                            {isSubmitting ? "Rejecting..." : "Reject"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>)}
              <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
                <button className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150" type="button" onClick={() => setSubmitEmployeeView(false)}>
                  {t("O_BACK")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
      
      {/* Form Viewer Modal */}
      {open && selectedForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-[960px] max-h-[90vh]">
            <FormViewer
              formStructure={selectedForm.structure}
              formData={selectedForm.data}
              onClose={handleCloseForm}
            />
          </div>
        </div>
      )}
      {documentOpen && selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          onClose={() => {
            setDocumentOpen(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </>
  );
};

export default SubmittedEmployeeView;